import React, { useState, useEffect, useRef } from 'react';
import { GetRequest, PostRequest } from 'components/common';
import { ContentPopup } from 'components/ContentPopup.jsx';

// Bulk mapping is cell-based, so it only applies to spreadsheet sources. Airtable (record/field
// based) is still linked one price at a time through the per-field linker.
const SPREADSHEET_SOURCE_TYPES = ['google_sheets', 'microsoft_excel'];

/**
 * Lets the user link many unit prices to cells in one spreadsheet at once, instead of opening the
 * per-field linker one price at a time. Pick a data source + spreadsheet + sheet once, then enter a
 * cell reference for each price to map, and save them all in a single request.
 */
const BulkMapDataSourceModal = ({ dataSources, onClose }) => {
  const popupRef = useRef();

  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [files, setFiles] = useState([]); // spreadsheets (Google) or workbooks (Excel)
  const [selectedFile, setSelectedFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null); // { id, name }
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [cellReferences, setCellReferences] = useState({}); // { [priceId]: 'B4' }
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState({}); // { [priceId]: { success, fetched_value, error } }

  const spreadsheetDataSources = (dataSources || []).filter(
    (dataSource) => SPREADSHEET_SOURCE_TYPES.includes(dataSource.source_type)
  );

  useEffect(() => {
    GetRequest({
      url: '/quote-builder/data-sources/mappable-prices',
      callback: (data) => {
        setPrices(data.prices || []);
        setLoadingPrices(false);
      }
    });
  }, []);

  const sourceTypeOf = (dataSourceId) => {
    const dataSource = spreadsheetDataSources.find((candidate) => candidate.id == dataSourceId);
    return dataSource ? dataSource.source_type : null;
  };

  const handleDataSourceSelect = (dataSourceId) => {
    setSelectedDataSource(dataSourceId);
    setFiles([]);
    setSelectedFile(null);
    setSheets([]);
    setSelectedSheet(null);
    setResults({});
    // A cell reference only means something for one sheet, so drop anything typed against the
    // previous spreadsheet rather than silently submitting it against the new one.
    setCellReferences({});

    if (!dataSourceId) {
      return;
    }

    const sourceType = sourceTypeOf(dataSourceId);
    const url = sourceType === 'microsoft_excel'
      ? `/quote-builder/data-sources/${dataSourceId}/workbooks`
      : `/quote-builder/data-sources/${dataSourceId}/spreadsheets`;

    setLoadingFiles(true);
    GetRequest({
      url,
      callback: (data) => {
        setFiles(data || []);
        setLoadingFiles(false);
      }
    });
  };

  const handleFileSelect = (fileId) => {
    setSelectedFile(fileId);
    setSheets([]);
    setSelectedSheet(null);
    setResults({});
    // Switching spreadsheet invalidates any cell references typed against the previous one.
    setCellReferences({});

    if (!fileId) {
      return;
    }

    const sourceType = sourceTypeOf(selectedDataSource);
    const url = sourceType === 'microsoft_excel'
      ? `/quote-builder/data-sources/${selectedDataSource}/workbook/${fileId}/worksheets`
      : `/quote-builder/data-sources/${selectedDataSource}/spreadsheet/${fileId}/sheets`;

    setLoadingFiles(true);
    GetRequest({
      url,
      callback: (data) => {
        setSheets(data || []);
        // Auto-select the first sheet so the common single-sheet case needs no extra click.
        if (data && data.length > 0) {
          setSelectedSheet({ id: data[0].id, name: data[0].name });
        }
        setLoadingFiles(false);
      }
    });
  };

  const handleSheetSelect = (sheetId) => {
    const sheet = sheets.find((candidate) => String(candidate.id) === String(sheetId));
    setSelectedSheet(sheet ? { id: sheet.id, name: sheet.name } : null);
  };

  const setCellReference = (priceId, value) => {
    setCellReferences((previous) => ({ ...previous, [priceId]: value }));
  };

  const selectedFileName = () => {
    const file = files.find((candidate) => String(candidate.id) === String(selectedFile));
    return file ? file.name : null;
  };

  const filledLinks = () => {
    return prices
      .filter((price) => cellReferences[price.id] && cellReferences[price.id].trim() !== '')
      .map((price) => ({
        user_unit_price_id: price.id,
        data_source_id: selectedDataSource,
        spreadsheet_id: selectedFile,
        spreadsheet_name: selectedFileName(),
        sheet_id: selectedSheet.id,
        sheet_name: selectedSheet.name,
        cell_reference: cellReferences[price.id].trim()
      }));
  };

  const canSave = !!(selectedDataSource && selectedFile && selectedSheet && filledLinks().length > 0);

  const handleSave = () => {
    const links = filledLinks();
    if (links.length === 0) {
      return;
    }

    setSaving(true);
    PostRequest({
      url: '/quote-builder/data-sources/bulk-link',
      data: { links },
      callback: (data) => {
        const byPriceId = {};
        (data.results || []).forEach((result) => {
          byPriceId[result.user_unit_price_id] = result;
        });
        setResults(byPriceId);
        setSaving(false);

        if (typeof trackEvent === 'function') {
          trackEvent('data_source_bulk_link_saved', { count: links.length });
        }
      }
    });
  };

  const errorMessageFor = (code) => {
    const messages = {
      value_not_fetched: 'quote_builder.bulk_map_error_value_not_fetched',
      data_source_not_found: 'quote_builder.bulk_map_error_data_source_not_found',
      price_not_found: 'quote_builder.bulk_map_error_price_not_found'
    };
    return t(messages[code] || 'quote_builder.bulk_map_error_generic');
  };

  const renderResult = (price) => {
    const result = results[price.id];
    if (!result) {
      if (price.has_data_source) {
        return <span className="label label-default">{t('quote_builder.bulk_map_already_linked')}</span>;
      }
      return null;
    }
    if (result.success) {
      return <span className="text-success"><i className="fa fa-check"></i> {result.fetched_value}</span>;
    }
    // The link is saved, but the cell value could not be read — flag it as a warning, not a hard
    // error, and never surface a raw server error message.
    const isWarning = result.error === 'value_not_fetched';
    return (
      <span className={isWarning ? 'text-warning' : 'text-danger'}>
        <i className={isWarning ? 'fa fa-exclamation-triangle' : 'fa fa-exclamation-circle'}></i> {errorMessageFor(result.error)}
      </span>
    );
  };

  const content = (
    <div className="bulk-map-data-source" style={{ textAlign: 'left' }}>
      <h3>{t('quote_builder.bulk_map_title')}</h3>
      <p className="text-muted">{t('quote_builder.bulk_map_intro')}</p>

      {spreadsheetDataSources.length === 0 ? (
        <div className="alert alert-info">{t('quote_builder.bulk_map_no_spreadsheet_sources')}</div>
      ) : (
        <>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ marginBottom: 0, whiteSpace: 'nowrap', minWidth: '110px' }}>{t('quote_builder.data_source')}</label>
            <select
              className="form-control"
              value={selectedDataSource || ''}
              onChange={(event) => handleDataSourceSelect(event.target.value)}
            >
              <option value="">{t('quote_builder.select_data_source')}</option>
              {spreadsheetDataSources.map((dataSource) => (
                <option key={dataSource.id} value={dataSource.id}>{dataSource.name}</option>
              ))}
            </select>
          </div>

          {selectedDataSource && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ marginBottom: 0, whiteSpace: 'nowrap', minWidth: '110px' }}>{t('quote_builder.spreadsheet')}</label>
              <select
                className="form-control"
                value={selectedFile || ''}
                onChange={(event) => handleFileSelect(event.target.value)}
              >
                <option value="">{t('quote_builder.select_spreadsheet')}</option>
                {files.map((file) => (
                  <option key={file.id} value={file.id}>{file.name}</option>
                ))}
              </select>
            </div>
          )}

          {selectedFile && sheets.length > 0 && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ marginBottom: 0, whiteSpace: 'nowrap', minWidth: '110px' }}>{t('quote_builder.sheet_tab')}</label>
              <select
                className="form-control"
                value={selectedSheet ? selectedSheet.id : ''}
                onChange={(event) => handleSheetSelect(event.target.value)}
              >
                {sheets.map((sheet) => (
                  <option key={sheet.id} value={sheet.id}>{sheet.name}</option>
                ))}
              </select>
            </div>
          )}

          {loadingFiles && (
            <p><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</p>
          )}

          <hr />

          {loadingPrices ? (
            <p><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</p>
          ) : prices.length === 0 ? (
            <div className="alert alert-info">{t('quote_builder.bulk_map_no_prices')}</div>
          ) : (
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>{t('quote_builder.bulk_map_price_column')}</th>
                    <th>{t('quote_builder.bulk_map_current_value_column')}</th>
                    <th style={{ width: '120px' }}>{t('quote_builder.cell_reference')}</th>
                    <th style={{ width: '140px' }}>{t('quote_builder.bulk_map_result_column')}</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((price) => (
                    <tr key={price.id}>
                      <td>{price.description}</td>
                      <td>{price.price_with_currency_formatted}</td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t('quote_builder.or_type_cell_reference')}
                          value={cellReferences[price.id] || ''}
                          disabled={!selectedSheet}
                          onChange={(event) => setCellReference(price.id, event.target.value)}
                        />
                      </td>
                      <td>{renderResult(price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="form-actions" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!canSave || saving}
            >
              {saving ? t('quote_builder.bulk_map_saving') : t('quote_builder.bulk_map_save')}
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={onClose}
            >
              {t('quote_builder.cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <ContentPopup
      myRef={popupRef}
      autoOpen={true}
      content={content}
      cancelButton={false}
      beforeClose={onClose}
    />
  );
};

export default BulkMapDataSourceModal;
